import * as React from "react";
import { useDeepCompareCallback } from "use-deep-compare";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { ApplicationStateEnum, GetApplicantsObject } from "../../network/APITypes";
import { generalStore } from "../../stores/GeneralStore";
import { hrStore } from "../../stores/HrStore";
import { TableStore } from "../../stores/TableStore";
import { GetApplicantsParams } from "../../types/models";

export const useApplicants = (
    options: GetApplicantsParams,
    tableStore?: TableStore,
    forceReloadApplicationState?: ApplicationStateEnum
) => {
    const nextPageRef = React.useRef(0);
    const inFlight = React.useRef(false);

    const [applicants, setApplicants] = React.useState<GetApplicantsObject[]>([]);
    const [initialLoaded, setInitialLoaded] = React.useState(false);
    const [total, setTotal] = React.useState(0);
    const [unreadApplicationsTotal, setUnreadApplicationsTotal] = React.useState<number | undefined>(0);

    const loadApplicants = useDeepCompareCallback(
        async (loadAll = false) => {
            if (inFlight.current === true) {
                return;
            }

            try {
                generalStore.isLoading = true;
                inFlight.current = true;

                const INITIAL_APPLICANTS_PER_COLUMN = 50;

                const response = await API.getApplicants({
                    limit: loadAll ? total : INITIAL_APPLICANTS_PER_COLUMN,
                    ...tableStore?.tableParams,
                    ...options
                });

                if (response && response.applicants) {
                    setApplicants(response.applicants);

                    setTotal(response.total);
                    setUnreadApplicationsTotal(response.unreadApplicationsTotal);
                    nextPageRef.current = nextPageRef.current + 1;
                }

                if (tableStore) {
                    tableStore.totalCount = response.total;
                }
            } catch (err) {
                generalStore.setError(t("error.loadApplications"), err);
            } finally {
                generalStore.isLoading = false;
                inFlight.current = false;
                setInitialLoaded(true);
            }
        },
        [options, tableStore?.tableParams]
    );

    const reload = React.useCallback(() => {
        loadApplicants();
    }, [loadApplicants]);

    React.useEffect(() => {
        reload();
    }, [reload]);

    React.useEffect(() => {
        if (forceReloadApplicationState === options.applicationState) {
            reload();

            if (forceReloadApplicationState) {
                hrStore.forceReloadApplicationState = undefined;
            }
        }
    }, [reload, forceReloadApplicationState, options.applicationState]);

    const loadMore = () => {
        loadApplicants(true);
    };

    return {
        applicants,
        initialLoaded,
        total,
        unreadApplicationsTotal,
        reload,
        loadMore: total > applicants.length ? loadMore : null
    };
};
